import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { BookOpen, Clock, Award, CheckCircle, Play, Monitor, Users, HelpCircle, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import GamificationCard from '../components/GamificationCard';
import Leaderboard from '../components/Leaderboard';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCourses, setRecentCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/student-dashboard`),
        axios.get(`${API_URL}/online-enrollments/my-courses`)
      ]);
      setStats(statsRes.data);
      setRecentCourses(coursesRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Welcome */}
        <div className="bg-gradient-to-r from-[#004E89] to-[#0EA5E9] rounded-lg p-6 text-white">
          <h1 className="font-manrope font-bold text-2xl mb-2">
            ¡Hola, {user?.nombre}!
          </h1>
          <p className="text-white/80">Bienvenido a tu panel de formación</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.cursos_online?.total || 0}</p>
                <p className="text-xs text-slate-500">Cursos online</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.cursos_online?.completados || 0}</p>
                <p className="text-xs text-slate-500">Completados</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatTime(stats?.tiempo_estudio_total || 0)}</p>
                <p className="text-xs text-slate-500">Tiempo estudio</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Award className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.certificados?.total || 0}</p>
                <p className="text-xs text-slate-500">Certificados</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quizzes Stats */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#004E89]" />
            Evaluaciones
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-3xl font-bold text-slate-900">{stats?.quizzes?.intentos_totales || 0}</p>
              <p className="text-sm text-slate-500">Intentos totales</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{stats?.quizzes?.aprobados || 0}</p>
              <p className="text-sm text-slate-500">Aprobados</p>
            </div>
          </div>
          <Button variant="outline" className="w-full mt-4" onClick={() => navigate('/my-quiz-history')}>
            Ver historial completo
          </Button>
        </div>

        {/* Recent Courses */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg">Mis cursos en progreso</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/my-courses')}>
              Ver todos
            </Button>
          </div>
          
          {recentCourses.length > 0 ? (
            <div className="space-y-4">
              {recentCourses.map((enrollment) => (
                <div key={enrollment.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#004E89] to-[#0EA5E9] flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-slate-900 truncate">{enrollment.course?.nombre}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={enrollment.progreso || 0} className="h-2 flex-1" />
                      <span className="text-sm font-medium text-slate-600">{Math.round(enrollment.progreso || 0)}%</span>
                    </div>
                  </div>
                  <Button size="sm" className="bg-[#004E89] hover:bg-[#003B66]" onClick={() => navigate(`/online-courses/${enrollment.online_course_id}`)}>
                    <Play className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No estás inscrito en ningún curso</p>
              <Button onClick={() => navigate('/online-courses')} className="mt-3 bg-[#004E89] hover:bg-[#003B66]">
                Explorar cursos
              </Button>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button variant="outline" className="h-auto py-4 flex flex-col gap-2" onClick={() => navigate('/online-courses')}>
            <Monitor className="w-6 h-6" />
            <span>Catálogo</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col gap-2" onClick={() => navigate('/my-courses')}>
            <BookOpen className="w-6 h-6" />
            <span>Mis Cursos</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col gap-2" onClick={() => navigate('/my-certificates')}>
            <Award className="w-6 h-6" />
            <span>Certificados</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col gap-2" onClick={() => navigate('/my-quiz-history')}>
            <HelpCircle className="w-6 h-6" />
            <span>Evaluaciones</span>
          </Button>
        </div>

        {/* Gamification Section */}
        <div className="grid lg:grid-cols-2 gap-6">
          <GamificationCard />
          <Leaderboard />
        </div>
      </div>
    </Layout>
  );
};

export default StudentDashboard;
